import { Static, Type } from '@sinclair/typebox';
import { AvailabilityBookabilitySchema } from './availability-bookability';
import { maxStrLength, ProPlacesResponseSchema, ProTeamMemberArrayContentResponseSchema } from './professional';
import { RECURRENCE_RRULE_MAX_LENGTH } from './recurrence-rule';
import { AvailabilityPrestationMode } from './translation_namespace/availabilities_namespace';
import { MINUTES_IN_DAY } from './utils';

const maxPlaceSelectable = 10;

/*
 * ─── Règles de réservation en ligne : délai minimum et horizon maximum ───────────
 *
 * Chaque règle a un réglage général (`pro_booking_params`) qu'une prestation peut remplacer
 * (`prestation.minimum_time_before_booking` / `prestation.horizon_bookable`, NULL = hérite), en
 * plus strict OU plus souple. Les résolveurs ci-dessous sont la SOURCE UNIQUE de la valeur
 * effective : ne jamais réécrire localement la chaîne `prestation ?? pro ?? défaut`, ni le
 * max / min d'un panier.
 */

// Délai minimum par défaut (heures) avant une réservation quand le pro n'a rien configuré —
// SOURCE UNIQUE partagée par le tunnel classique (availability.service) et le tunnel VTC
// (ride.service.checkRideInstantsBookable). Dès que la prestation est connue, passer par les
// résolveurs ci-dessous plutôt que par un `?? DEFAULT_MIN_BOOKING_DELAY_HOURS` local.
export const DEFAULT_MIN_BOOKING_DELAY_HOURS = 24;

/**
 * Délai minimum effectif (heures) d'UNE prestation : son réglage propre
 * (`prestation.minimum_time_before_booking`), sinon le réglage général du pro
 * (`pro_booking_params.minimum_time_before_booking`), sinon {@link DEFAULT_MIN_BOOKING_DELAY_HOURS}.
 * Le réglage propre peut être plus long OU plus court que le général. `0` (réservation
 * immédiate) est une valeur, pas une absence : `??`, jamais `||`.
 */
export const resolvePrestationMinBookingDelayHours = (
  prestationDelayHours: number | null | undefined,
  proDelayHours: number | null | undefined,
): number => prestationDelayHours ?? proDelayHours ?? DEFAULT_MIN_BOOKING_DELAY_HOURS;

/**
 * Délai minimum effectif (heures) d'un panier : le plus long des délais effectifs de ses items,
 * appliqué au DÉBUT de la réservation (premier créneau de la chaîne). Un item sans prestation
 * (ou introuvable) passe `undefined` et hérite donc du réglage général ; panier vide ⇒ réglage
 * général. Choke point unique du MAX panier (moteur de créneaux, donc sondes du tunnel, et
 * pré-réservation) : ne pas réimplémenter le max en local.
 *
 * Ce n'est PAS la règle d'un ensemble de candidats : une formule Course isolée passe par
 * {@link resolvePrestationMinBookingDelayHours}, et « peu importe » (VTC) filtre ses formules
 * candidates une à une (cf. ride.service) — un max y refuserait une course qu'une formule plus
 * souple accepte.
 */
export const resolveCartMinBookingDelayHours = (
  prestationDelayHours: ReadonlyArray<number | null | undefined>,
  proDelayHours: number | null | undefined,
): number =>
  prestationDelayHours.length === 0
    ? resolvePrestationMinBookingDelayHours(undefined, proDelayHours)
    : Math.max(
        ...prestationDelayHours.map((delayHours): number =>
          resolvePrestationMinBookingDelayHours(delayHours, proDelayHours),
        ),
      );

/**
 * Délai minimum effectif le plus COURT (heures) parmi des prestations CANDIDATES (une seule
 * réservable suffit), à l'inverse du panier où toutes doivent l'être. Borne ce qu'on ne peut pas
 * refuser tant que la prestation n'est pas choisie : pré-contrôle « peu importe » des courses
 * (aucune formule n'accepte une prise en charge plus proche). Pendant de
 * {@link resolveLongestBookingHorizonDays}. Item `null` / `undefined` ⇒ réglage général ; ensemble
 * vide ⇒ réglage général.
 */
export const resolveShortestMinBookingDelayHours = (
  prestationDelayHours: ReadonlyArray<number | null | undefined>,
  proDelayHours: number | null | undefined,
): number =>
  prestationDelayHours.length === 0
    ? resolvePrestationMinBookingDelayHours(undefined, proDelayHours)
    : Math.min(
        ...prestationDelayHours.map((delayHours): number =>
          resolvePrestationMinBookingDelayHours(delayHours, proDelayHours),
        ),
      );

// Horizon de réservation (jours) quand le pro n'a pas de ligne `pro_booking_params` : repli de
// LECTURE, jamais une valeur écrivable (les options de BOOKING_HORIZON_BOOKABLE_DAYS restent en
// dessous). Dès que la prestation est connue, passer par les résolveurs ci-dessous plutôt que par
// un `?? MAX_BOOKING_HORIZON_DAYS` local.
export const MAX_BOOKING_HORIZON_DAYS = 400;

/**
 * Horizon de réservation effectif (jours) d'UNE prestation : son réglage propre
 * (`prestation.horizon_bookable`), sinon le réglage général du pro
 * (`pro_booking_params.horizon_bookable`), sinon {@link MAX_BOOKING_HORIZON_DAYS}.
 * Le réglage propre peut être plus court OU plus long que le général. `0` est une valeur, pas
 * une absence : `??`, jamais `||`.
 */
export const resolvePrestationBookingHorizonDays = (
  prestationDays: number | null | undefined,
  proDays: number | null | undefined,
): number => prestationDays ?? proDays ?? MAX_BOOKING_HORIZON_DAYS;

/**
 * Horizon effectif (jours) d'un panier : le plus COURT des horizons effectifs de ses items,
 * appliqué au DÉBUT de la réservation (début ≤ maintenant + horizon). Un item sans prestation
 * (ou introuvable) passe `undefined` et hérite donc du réglage général ; panier vide ⇒ réglage
 * général. Choke point unique du MIN panier (moteur de créneaux, donc sondes du tunnel, et
 * pré-réservation) : ne pas réimplémenter le min en local.
 *
 * Ce n'est PAS la règle d'un ensemble de candidats (formules « peu importe », prestations
 * servies par un planning…) : voir {@link resolveLongestBookingHorizonDays}.
 */
export const resolveCartBookingHorizonDays = (
  prestationDays: ReadonlyArray<number | null | undefined>,
  proDays: number | null | undefined,
): number =>
  prestationDays.length === 0
    ? resolvePrestationBookingHorizonDays(undefined, proDays)
    : Math.min(...prestationDays.map((days): number => resolvePrestationBookingHorizonDays(days, proDays)));

/**
 * Horizon effectif le plus LONG (jours) parmi des prestations CANDIDATES (une seule réservable
 * suffit), à l'inverse du panier où toutes doivent l'être. Borne ce qu'on ne peut pas écarter
 * tant que la prestation n'est pas choisie : plannings chargés par les sondes du tunnel (une plage
 * qui ne démarre que dans l'horizon d'une prestation plus longue doit rester), diagnostic
 * BEYOND_HORIZON d'un planning (prestations en ligne qu'il sert), formules Course servies.
 * Item `null` / `undefined` ⇒ réglage général ; ensemble vide ⇒ réglage général.
 */
export const resolveLongestBookingHorizonDays = (
  prestationDays: ReadonlyArray<number | null | undefined>,
  proDays: number | null | undefined,
): number =>
  prestationDays.length === 0
    ? resolvePrestationBookingHorizonDays(undefined, proDays)
    : Math.max(...prestationDays.map((days): number => resolvePrestationBookingHorizonDays(days, proDays)));

/*
 * Bornes de la fenêtre de réservation en ligne, en instants absolus (ms) — SOURCE UNIQUE pour le
 * moteur de créneaux et ses sondes, la pré-réservation, les courses et le diagnostic des plannings,
 * qui doivent rester d'accord à la milliseconde près (un créneau listé puis refusé au checkout, ou
 * l'inverse, sinon). Délai en heures, horizon en jours de 24 h (pas en jours calendaires).
 */
const BOOKING_WINDOW_HOUR_MS = 60 * 60 * 1000;
const BOOKING_WINDOW_DAY_MS = 24 * BOOKING_WINDOW_HOUR_MS;

/** Départ le plus tôt réservable : maintenant + délai minimum. Un départ STRICTEMENT antérieur est trop tôt. */
export const resolveEarliestBookableStartMs = (nowMs: number, minDelayHours: number): number =>
  nowMs + minDelayHours * BOOKING_WINDOW_HOUR_MS;

/** Départ le plus tard réservable : maintenant + horizon, borne INCLUSE. Un départ strictement postérieur est trop loin. */
export const resolveLatestBookableStartMs = (nowMs: number, horizonDays: number): number =>
  nowMs + horizonDays * BOOKING_WINDOW_DAY_MS;

export type BookingWindowRefusal = 'TOO_SOON' | 'TOO_FAR';

/**
 * Verdict d'un départ face à la fenêtre de réservation : `TOO_SOON` (délai d'abord), `TOO_FAR`, ou
 * `null` si réservable. `minNoticeGraceMs` (pré-réservation uniquement) recule la borne basse d'une
 * marge de grâce, sans jamais la faire passer avant « maintenant » : un départ déjà commencé n'est
 * jamais réservable.
 */
export const resolveBookingWindowRefusal = (
  startMs: number,
  nowMs: number,
  minDelayHours: number,
  horizonDays: number,
  options: { minNoticeGraceMs?: number } = {},
): BookingWindowRefusal | null => {
  const earliestStartMs = Math.max(
    nowMs,
    resolveEarliestBookableStartMs(nowMs, minDelayHours) - (options.minNoticeGraceMs ?? 0),
  );
  if (startMs < earliestStartMs) return 'TOO_SOON';
  if (startMs > resolveLatestBookableStartMs(nowMs, horizonDays)) return 'TOO_FAR';
  return null;
};

//⚠️IMPACT attention impact dans le frontend enabledParameters[TimeSlotParameterType
export enum TimeSlotParameterType {
  PRO_PLACE = 'PRO_PLACE',
  RESTRICTED_TAGS = 'RESTRICTED_TAGS',
  PRESTATION = 'PRESTATION',
}
export const TimeSlotParameterToFieldName: Record<TimeSlotParameterType, keyof TimeSlotParametersRequestObject> = {
  [TimeSlotParameterType.PRO_PLACE]: 'pro_places',
  [TimeSlotParameterType.RESTRICTED_TAGS]: 'restricted_tag_ids',
  [TimeSlotParameterType.PRESTATION]: 'prestation_mode',
};

export const TimeSlotParametersResponseSchema = Type.Object({
  pro_places: Type.Optional(Type.Array(ProPlacesResponseSchema)),
  // Restriction « qui peut réserver » par tag pour ce créneau (override de la dispo).
  restricted_tag_ids: Type.Optional(
    Type.Array(Type.Number(), { meta: { input_type: 'pro_tag_selector', show_all_customers: true } }),
  ),
  restricted_tags_overridden: Type.Optional(Type.Boolean()),
  prestation_mode: Type.Optional(Type.String()),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  prestation_category_ids: Type.Optional(Type.Array(Type.Number())),
});

export const TimeSlotParametersRequestSchema = Type.Object({
  pro_places: Type.Optional(Type.Array(Type.Number())),
  restricted_tag_ids: Type.Optional(
    Type.Array(Type.Number(), { meta: { input_type: 'pro_tag_selector', show_all_customers: true } }),
  ),
  restricted_tags_overridden: Type.Optional(Type.Boolean()),
  prestation_mode: Type.Optional(Type.String()),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  prestation_category_ids: Type.Optional(Type.Array(Type.Number())),
});

export type TimeSlotParametersResponseObject = Static<typeof TimeSlotParametersResponseSchema>;
export type TimeSlotParametersRequestObject = Static<typeof TimeSlotParametersRequestSchema>;

export { AvailabilityPrestationMode };

export const TimeSlotResponseSchema = Type.Object({
  day: Type.Number(),
  start: Type.Number(),
  end: Type.Number(),
  parameters: Type.Optional(TimeSlotParametersResponseSchema),
});

// Bornes en minutes : end = 1440 (« 24:00 ») = fin de journée — convention « minuit fin de
// plage ». Sans borne haute, un end > 1440 produirait « 25:00 » → Invalid Date → RangeError
// à l'expansion calendrier (availability.service).
export const TimeSlotRequestSchema = Type.Object({
  day: Type.Number({ minimum: 0, maximum: 6 }),
  start: Type.Number({ minimum: 0, maximum: MINUTES_IN_DAY - 1 }),
  end: Type.Number({ minimum: 0, maximum: MINUTES_IN_DAY }),
  parameters: Type.Optional(TimeSlotParametersRequestSchema),
});

export type TimeSlotResponseObject = Static<typeof TimeSlotResponseSchema>;
export type TimeSlotRequestObject = Static<typeof TimeSlotRequestSchema>;

export const SpecificDateSlotResponseSchema = Type.Object({
  date: Type.String({ format: 'date' }),
  start: Type.Number(),
  end: Type.Number(),
  parameters: Type.Optional(TimeSlotParametersResponseSchema),
  team_members_overridden: Type.Optional(Type.Boolean()),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
});

export type SpecificDateSlotResponseObject = Static<typeof SpecificDateSlotResponseSchema>;

export const SpecificDateSlotRequestSchema = Type.Object({
  date: Type.String({ format: 'date' }),
  start: Type.Number({ minimum: 0, maximum: MINUTES_IN_DAY - 1 }),
  end: Type.Number({ minimum: 0, maximum: MINUTES_IN_DAY }),
  parameters: Type.Optional(TimeSlotParametersRequestSchema),
  team_members_overridden: Type.Optional(Type.Boolean()),
  team_member_ids: Type.Optional(Type.Array(Type.Integer({ minimum: 1 }), { uniqueItems: true })),
});

export type SpecificDateSlotRequestObject = Static<typeof SpecificDateSlotRequestSchema>;

/** Une sélection personnalisée vide reste vide, notamment après suppression de ses membres. */
export const resolveAvailabilityTeamMemberIds = (
  defaultMemberIds: readonly number[],
  specificDate?: { team_members_overridden?: boolean; team_member_ids?: readonly number[] },
): number[] => [
  ...new Set(specificDate?.team_members_overridden ? (specificDate.team_member_ids ?? []) : defaultMemberIds),
];

/**
 * Tous les membres nommés par un planning : ses membres par défaut et ceux de ses dates
 * personnalisées. Périmètre des gardes « ses propres données » (un planning est à soi quand
 * il ne nomme que soi), partagé par le backend et son miroir UI.
 */
export const resolveAvailabilityReferencedTeamMemberIds = (
  defaultMemberIds: readonly number[],
  specificDates: readonly { team_members_overridden?: boolean; team_member_ids?: readonly number[] }[],
): number[] => [
  ...new Set([
    ...defaultMemberIds,
    ...specificDates.flatMap((specificDate): readonly number[] =>
      specificDate.team_members_overridden ? (specificDate.team_member_ids ?? []) : [],
    ),
  ]),
];

export const CreateAvailabilitySchema = Type.Object({
  name: Type.String({
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_NAME',
    title: 'INPUT_TITLE_PLANNING_NAME',
    minLength: 1,
    maxLength: maxStrLength,
  }),
  // Aucun lieu autorisé (décision produit) : les courses y restent réservables, sans limite de
  // zone. Le diagnostic de réservabilité signale le cas (NO_ACTIVE_PLACE pour les prestations hors
  // courses, RIDES_WITHOUT_ZONE pour les courses).
  pro_place: Type.Array(Type.Number(), {
    maxItems: maxPlaceSelectable,
    meta: { input_type: 'pro_place_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_PLACES',
    title: 'INPUT_TITLE_PLANNING_PLACES',
  }),
  // Aucun membre autorisé : planning d'ouverture d'une salle ou d'un équipement. Ses créneaux ne
  // servent alors QUE les prestations en libre-service (garde du moteur), jamais une prestation
  // qui exige un intervenant (diagnostic MEMBERLESS_SELF_SERVICE_ONLY / NO_ACTIVE_MEMBER).
  team_members: Type.Array(Type.Number(), {
    minItems: 0,
    meta: { input_type: 'team_member_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_TEAM_MEMBERS',
    title: 'INPUT_TITLE_PLANNING_TEAM_MEMBERS',
  }),
  // Restriction « qui peut réserver » : aucun tag = tout le monde ; sinon le client doit
  // porter TOUS les tags (ET). Remplace l'ancien enum booking_eligibility (inerte).
  restricted_tag_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { input_type: 'pro_tag_selector', show_all_customers: true },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_RESTRICTED_TAGS',
      title: 'INPUT_TITLE_PLANNING_RESTRICTED_TAGS',
    }),
  ),
  prestation_mode: Type.Optional(
    Type.String({
      title: 'INPUT_TITLE_PLANNING_PRESTATION_MODE',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_PRESTATION_MODE',
      meta: { input_type: 'availability_prestation_selector' },
    }),
  ),
  prestation_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { hidden: true },
    }),
  ),
  prestation_category_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { hidden: true },
    }),
  ),
  slots: Type.Array(TimeSlotRequestSchema, {
    meta: { input_type: 'time_slots_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_SLOTS',
    title: 'INPUT_TITLE_PLANNING_SLOTS',
  }),
  // Règle d'activation RRULE du gabarit (sous-ensemble whitelisté, cf.
  // availability-recurrence-rule.ts). Sur le fil, « chaque semaine » =
  // ALWAYS_ACTIVE_AVAILABILITY_RRULE ; stocké sous sa forme canonique explicite (jamais NULL).
  recurrence_rrule: Type.Optional(
    Type.String({
      maxLength: RECURRENCE_RRULE_MAX_LENGTH,
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_RECURRENCE',
      title: 'INPUT_TITLE_PLANNING_RECURRENCE',
      meta: { input_type: 'availability_recurrence_selector' },
    }),
  ),
  start: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_START_DATE',
      title: 'INPUT_TITLE_PLANNING_START_DATE',
      // Pré-rempli à la date du jour dans le formulaire (DynamicForm/buildSafeDefaults).
      // Sans plages hebdomadaires, le backend dérive la période des dates ponctuelles.
      meta: { default_today: true },
    }),
  ),
  end: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_END_DATE',
      title: 'INPUT_TITLE_PLANNING_END_DATE',
      meta: { must_be_after: 'start' },
    }),
  ),
  specific_dates: Type.Optional(
    Type.Array(SpecificDateSlotRequestSchema, {
      meta: { input_type: 'specific_dates_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_SPECIFIC_DATES',
      title: 'INPUT_TITLE_PLANNING_SPECIFIC_DATES',
    }),
  ),
});

export type CreateAvailabilityObject = Static<typeof CreateAvailabilitySchema>;

export const AvailabilityResponseSchema = Type.Object({
  availability_id: Type.Number(),
  name: Type.String({
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_NAME',
    title: 'INPUT_TITLE_PLANNING_NAME',
    minLength: 1,
    maxLength: maxStrLength,
  }),
  // Optionnels : `transformToSchema` sérialise tout tableau vide en `null` (ex. planning
  // dont tous les membres / lieux liés ont été supprimés) — le type doit le refléter.
  pro_place: Type.Optional(Type.Array(ProPlacesResponseSchema)),
  team_members: Type.Optional(Type.Array(ProTeamMemberArrayContentResponseSchema)),
  restricted_tag_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { input_type: 'pro_tag_selector', show_all_customers: true },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_RESTRICTED_TAGS',
      title: 'INPUT_TITLE_PLANNING_RESTRICTED_TAGS',
    }),
  ),
  prestation_mode: Type.Optional(Type.String()),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  prestation_category_ids: Type.Optional(Type.Array(Type.Number())),
  slots: Type.Array(TimeSlotResponseSchema, {
    meta: { input_type: 'time_slots_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_SLOTS',
    title: 'INPUT_TITLE_PLANNING_SLOTS',
  }),
  // Toujours présente (colonne NOT NULL DEFAULT — la règle est explicite en base).
  recurrence_rrule: Type.String({
    maxLength: RECURRENCE_RRULE_MAX_LENGTH,
    placeholder: 'INPUT_PLACEHOLDER_PLANNING_RECURRENCE',
    title: 'INPUT_TITLE_PLANNING_RECURRENCE',
    meta: { input_type: 'availability_recurrence_selector' },
  }),
  // Libellé humain de la règle, formaté backend (formatRecurrenceRuleLabel).
  recurrence_label: Type.Optional(Type.String({ meta: { hidden: true } })),
  // Réservabilité en ligne calculée backend (règles H1-H12) — jamais persistée ni saisie.
  bookability: Type.Optional(AvailabilityBookabilitySchema),
  start: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_START_DATE',
      title: 'INPUT_TITLE_PLANNING_START_DATE',
    }),
  ),
  end: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_END_DATE',
      title: 'INPUT_TITLE_PLANNING_END_DATE',
    }),
  ),
  specific_dates: Type.Optional(
    Type.Array(SpecificDateSlotResponseSchema, {
      meta: { input_type: 'specific_dates_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_SPECIFIC_DATES',
      title: 'INPUT_TITLE_PLANNING_SPECIFIC_DATES',
    }),
  ),
});

export type AvailabilityResponseObject = Static<typeof AvailabilityResponseSchema>;

export const UpdateAvailabilitySchema = Type.Object({
  name: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_NAME',
      title: 'INPUT_TITLE_PLANNING_NAME',
      minLength: 1,
      maxLength: maxStrLength,
    }),
  ),
  // `[]` = retirer tous les lieux (cf. CreateAvailabilitySchema) ; absent = inchangé.
  pro_place: Type.Optional(
    Type.Array(Type.Number(), {
      maxItems: maxPlaceSelectable,
      meta: { input_type: 'pro_place_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_PLACES',
      title: 'INPUT_TITLE_PLANNING_PLACES',
    }),
  ),
  team_members: Type.Optional(
    Type.Array(Type.Number(), {
      minItems: 0,
      meta: { input_type: 'team_member_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_TEAM_MEMBERS',
      title: 'INPUT_TITLE_PLANNING_TEAM_MEMBERS',
    }),
  ),
  restricted_tag_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { input_type: 'pro_tag_selector', show_all_customers: true },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_RESTRICTED_TAGS',
      title: 'INPUT_TITLE_PLANNING_RESTRICTED_TAGS',
    }),
  ),
  prestation_mode: Type.Optional(
    Type.String({
      title: 'INPUT_TITLE_PLANNING_PRESTATION_MODE',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_PRESTATION_MODE',
      meta: { input_type: 'availability_prestation_selector' },
    }),
  ),
  prestation_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { hidden: true },
    }),
  ),
  prestation_category_ids: Type.Optional(
    Type.Array(Type.Number(), {
      meta: { hidden: true },
    }),
  ),
  slots: Type.Optional(
    Type.Array(TimeSlotRequestSchema, {
      meta: { input_type: 'time_slots_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_SLOTS',
      title: 'INPUT_TITLE_PLANNING_SLOTS',
    }),
  ),
  recurrence_rrule: Type.Optional(
    Type.String({
      maxLength: RECURRENCE_RRULE_MAX_LENGTH,
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_RECURRENCE',
      title: 'INPUT_TITLE_PLANNING_RECURRENCE',
      meta: { input_type: 'availability_recurrence_selector' },
    }),
  ),
  start: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_START_DATE',
      title: 'INPUT_TITLE_PLANNING_START_DATE',
    }),
  ),
  end: Type.Optional(
    Type.String({
      format: 'date-time',
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_END_DATE',
      title: 'INPUT_TITLE_PLANNING_END_DATE',
      meta: { must_be_after: 'start' },
    }),
  ),
  specific_dates: Type.Optional(
    Type.Array(SpecificDateSlotRequestSchema, {
      meta: { input_type: 'specific_dates_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PLANNING_SPECIFIC_DATES',
      title: 'INPUT_TITLE_PLANNING_SPECIFIC_DATES',
    }),
  ),
});

export type UpdateAvailabilityObject = Static<typeof UpdateAvailabilitySchema>;

export const BookableAvailabilityItemSchema = Type.Object({
  prestation_id: Type.Optional(Type.Number()),
  pro_place_id: Type.Optional(Type.Number()),
  team_member_id: Type.Optional(Type.Number()),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  duration: Type.Integer({ minimum: 1 }),
  quantity: Type.Integer({ minimum: 1 }),
  selected_price_index: Type.Optional(Type.Integer({ minimum: 0 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  pro_resource_ids: Type.Optional(Type.Array(Type.Integer())),
  party_size: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type BookableAvailabilityItemObject = Static<typeof BookableAvailabilityItemSchema>;

/*
 * Séances de groupe : inclure aussi les créneaux dont les places restantes sont inférieures au
 * `party_size` demandé (y compris complets, `remaining_places` = 0), pour affichage grisé côté UI.
 * SANS ce drapeau, le contrat historique tient : tout créneau renvoyé est réservable tel quel
 * (outils MCP, sondes, validation de reschedule s'y appuient — ne jamais le forwarder là-bas).
 */
export const IncludeSessionSlotsBelowPartySizeSchema = Type.Optional(
  Type.Boolean({
    description:
      'Séances de groupe : inclure aussi les créneaux dont les places restantes sont inférieures au party_size demandé (y compris complets, remaining_places = 0). Ces créneaux ne sont PAS réservables tels quels : griser quand remaining_places < party_size. Sans ce drapeau, tout créneau renvoyé est réservable.',
  }),
);

export const BookableAvailabilityRequestSchema = Type.Object({
  professional_id: Type.Number(),
  start_date: Type.Optional(Type.String({ format: 'date-time' })),
  slots: Type.Array(BookableAvailabilityItemSchema, { minItems: 1 }),
  tolerance_minutes: Type.Optional(Type.Integer({ minimum: 0 })),
  exclude_reservation_id: Type.Optional(Type.Number()),
  include_session_slots_below_party_size: IncludeSessionSlotsBelowPartySizeSchema,
});
export type BookableAvailabilityRequestObject = Static<typeof BookableAvailabilityRequestSchema>;

// slot_start/slot_end sont des instants absolus UTC produits par le moteur de dispo et renvoyés
// tels quels par le client (reschedule). Surtout PAS de format: 'date-time' ici : sur les routes
// authentifiées, transformDateTimeFields (TypeBoxBody) convertirait ces chaînes déjà suffixées Z
// comme des heures murales → décalage de -2h en prod (serveur UTC). Le pattern garde la validation
// de forme sans déclencher la conversion (qui ne teste que `format`).
const UTC_INSTANT_PATTERN = '^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{1,3})?Z$';

export const BookableSlotItemSchema = Type.Object({
  slot_start: Type.String({ pattern: UTC_INSTANT_PATTERN }),
  slot_end: Type.String({ pattern: UTC_INSTANT_PATTERN }),
  duration: Type.Integer({ minimum: 1 }),
  team_member_id: Type.Optional(Type.Number()),
  pro_place_id: Type.Optional(Type.Number()),
  prestation_id: Type.Optional(Type.Number()),
  quantity: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_price_index: Type.Optional(Type.Integer({ minimum: 0 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  /*
   * Places restantes au moment du listing, valeur d'AFFICHAGE (« 4/10 ») — jamais une règle :
   * son unité dépend de la ressource (personnes pour une séance ou une ressource partagée,
   * unités pour une ressource exclusive ou un pool), donc le client ne doit RIEN en déduire.
   * Le verdict « ce créneau accueille-t-il le groupe demandé ? » est porté par
   * `below_requested_party`, calculé par le moteur.
   * Absent sans plafond ni ressource. `minimum: 0` volontaire (0 est signifiant) ; pas de suffixe
   * `_string` (piège source_key de transformToSchema).
   */
  remaining_places: Type.Optional(Type.Integer({ minimum: 0 })),
  // Capacité totale dans la même unité (dénominateur d'affichage « 4/10 »).
  capacity_total: Type.Optional(Type.Integer({ minimum: 1 })),
  /*
   * `true` ⇒ l'item est listé mais ne peut PAS absorber le groupe demandé : le client l'affiche
   * grisé, avec son compteur. Émis par le moteur, qui seul connaît l'unité des places et la règle
   * de consommation (cf. `resourceQuantityForBooking` côté backend) — c'était la redérivation
   * côté client qui produisait des grisages faux dès que « place » ne voulait pas dire « personne ».
   * N'apparaît aujourd'hui que sur les séances sous-capacité (`include_session_slots_below_party_size`) :
   * hors séance, un créneau trop petit n'est pas listé du tout.
   */
  below_requested_party: Type.Optional(Type.Boolean()),
});
export type BookableSlotItemObject = Static<typeof BookableSlotItemSchema>;

export const BookableSlotSchema = Type.Object({
  slot_start: Type.String({ format: 'date-time' }),
  slot_start_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'slot_start', is_short: true, with_time: true },
    }),
  ),
  slot_start_time_string: Type.Optional(Type.String({ meta: { source_key: 'slot_start', format_as: 'time' } })),
  slot_end: Type.String({ format: 'date-time' }),
  slot_end_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'slot_end', is_short: true, with_time: true },
    }),
  ),
  slot_end_time_string: Type.Optional(Type.String({ meta: { source_key: 'slot_end', format_as: 'time' } })),
  slot_date: Type.Optional(Type.String()),
  day: Type.Number(),
  team_member_id: Type.Optional(Type.Number()),
  quantity: Type.Optional(Type.Integer({ minimum: 1 })),
  slots: Type.Optional(Type.Array(BookableSlotItemSchema)),
  // MIN des `remaining_places` des items séance de la chaîne — absent si aucun item séance plafonné.
  remaining_places: Type.Optional(Type.Integer({ minimum: 0 })),
  // `true` dès qu'UN item de la chaîne ne peut pas absorber le groupe demandé (cf. l'item).
  below_requested_party: Type.Optional(Type.Boolean()),
  /*
   * Lot 4 Promotions : promotion automatique qui dépend de l'heure ou des dates du rendez-vous et que ce
   * créneau déclenche (chaque article jugé sur son propre début). Marquage du créneau (« −15 % »), nom de
   * l'offre et heures pour l'en-tête du jour (« de 10h00 à 12h00 »). Absents : rien à signaler.
   */
  promotion_label: Type.Optional(Type.String()),
  promotion_name: Type.Optional(Type.String()),
  promotion_hours_label: Type.Optional(Type.String()),
});
export type BookableSlotObject = Static<typeof BookableSlotSchema>;

export const BookableAvailabilityResponseSchema = Type.Array(BookableSlotSchema);
export type BookableAvailabilityResponseObject = Static<typeof BookableAvailabilityResponseSchema>;

/**
 * POST :slug/bookable/next (P3) : prochain créneau quand la semaine affichée est vide, une seule recherche
 * bornée à l'horizon du panier, avec la sélection en cours. Dates formatées par le serveur (locale du pro).
 * `slot`, `slot_start` et `horizon_end` : instants UTC renvoyés tels quels (même contrat que `/bookable`,
 * le créneau se renvoie à la réservation) ; seuls les `*_formatted` sont des libellés.
 */
export const BookableNextSlotResponseSchema = Type.Object({
  // Premier créneau trouvé, tel que listé par `/bookable` (déjà formaté une fois) ; absent : aucun d'ici la fin de l'horizon.
  slot: Type.Optional(BookableSlotSchema),
  slot_start: Type.Optional(Type.String({ format: 'date-time' })),
  // « jeudi 5 novembre 2026 à 10:00 » : bouton « Prochain créneau : … ».
  slot_start_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'slot_start', with_time: true } }),
  ),
  // Dernier départ réservable : « Aucun créneau disponible d'ici le … ».
  horizon_end: Type.Optional(Type.String({ format: 'date-time' })),
  horizon_end_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'horizon_end' } })),
});
export type BookableNextSlotResponseObject = Static<typeof BookableNextSlotResponseSchema>;

// ─── Places restantes (séances de groupe) — helpers d'affichage partagés ───
// Typés structurellement pour servir à la fois le tunnel (types TypeBox directs) et le picker pro
// (types Orval). Choke point unique de la décision de grisage — ne pas la réimplémenter en local.

interface SlotWithRemainingPlaces {
  remaining_places?: number;
  below_requested_party?: boolean;
  slots?: { remaining_places?: number; below_requested_party?: boolean }[];
}

/** Places restantes affichables d'un créneau (MIN des items séance, calculé backend). `undefined` = pas une séance plafonnée. */
export const getSlotRemainingPlaces = (slot: SlotWithRemainingPlaces): number | undefined => slot.remaining_places;

/**
 * `true` quand le créneau est listé mais ne peut PAS absorber le groupe demandé → grisé
 * (non cliquable), compteur visible.
 *
 * Simple LECTURE du verdict backend (`below_requested_party`) : l'unité de `remaining_places`
 * dépend de la ressource (personnes pour une séance ou une salle partagée, unités pour une
 * cabine exclusive ou un pool), donc la comparer ici à un nombre de personnes produisait des
 * grisages faux — un couple refusé sur la dernière cabine libre, par exemple.
 */
export const isSlotBelowRequestedPartySize = (slot: SlotWithRemainingPlaces): boolean =>
  slot.below_requested_party === true ||
  (slot.slots ?? []).some((itemSlot): boolean => itemSlot.below_requested_party === true);

export const AvailabilityDaySlotSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  availability_id: Type.Number(),
  name: Type.String(),
  restricted_tag_ids: Type.Optional(Type.Array(Type.Number())),
  team_member_ids: Type.Array(Type.Number()),
  availability_team_member_ids: Type.Optional(Type.Array(Type.Number())),
  // Lieux EFFECTIFS du créneau : la surcharge de créneau (availability_slot_parameter.pro_places)
  // REMPLACE les lieux du planning, et son sélecteur n'est pas borné aux lieux du planning. Un
  // consommateur qui retomberait sur `availability.pro_place` construirait donc un périmètre faux
  // (cf. _getEffectiveProPlaceIds). La valeur est déjà calculée côté moteur : on l'expose.
  pro_place_ids: Type.Optional(Type.Array(Type.Number())),
  // Périmètre prestation EFFECTIF du créneau (surcharge de créneau déjà résolue). Même raison que
  // `pro_place_ids` : la surcharge remplace le périmètre du planning, et le front ne peut pas la
  // recalculer sans réapparier l'instant à l'heure murale du pro.
  prestation_mode: Type.Optional(Type.String()),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  prestation_category_ids: Type.Optional(Type.Array(Type.Number())),
  is_unavailable: Type.Optional(Type.Boolean()),
  unavailable_slot_id: Type.Optional(Type.Number()),
});
export type AvailabilityDaySlotObject = Static<typeof AvailabilityDaySlotSchema>;

export const AvailabilityDaySchema = Type.Object({
  date: Type.String({ format: 'date' }),
  slots: Type.Array(AvailabilityDaySlotSchema),
});
export type AvailabilityDayObject = Static<typeof AvailabilityDaySchema>;

export const AvailabilityCalendarResponseSchema = Type.Array(AvailabilityDaySchema);
export type AvailabilityCalendarResponseObject = Static<typeof AvailabilityCalendarResponseSchema>;

export const SlotAppointmentCountsRequestSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Array(Type.Number()),
});
export type SlotAppointmentCountsRequestObject = Static<typeof SlotAppointmentCountsRequestSchema>;

export const SlotAppointmentCountItemSchema = Type.Object({
  team_member_id: Type.Number(),
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  appointment_count: Type.Number(),
});
export type SlotAppointmentCountItemObject = Static<typeof SlotAppointmentCountItemSchema>;

export const SlotAppointmentCountsResponseSchema = Type.Array(SlotAppointmentCountItemSchema);
export type SlotAppointmentCountsResponseObject = Static<typeof SlotAppointmentCountsResponseSchema>;

export const MarkUnavailableRequestSchema = Type.Object({
  availability_id: Type.Number(),
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  cancel_appointments: Type.Boolean(),
  cancellation_reason: Type.Optional(Type.String({ maxLength: 1000 })),
});
export type MarkUnavailableRequestObject = Static<typeof MarkUnavailableRequestSchema>;

export const MarkUnavailableResponseSchema = Type.Object({
  unavailable_slot_ids: Type.Array(Type.Number()),
});
export type MarkUnavailableResponseObject = Static<typeof MarkUnavailableResponseSchema>;

export const MarkUnavailableRangeRequestSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  cancel_appointments: Type.Boolean(),
  cancellation_reason: Type.Optional(Type.String({ maxLength: 1000 })),
});
export type MarkUnavailableRangeRequestObject = Static<typeof MarkUnavailableRangeRequestSchema>;

export const MarkUnavailableRangeResponseSchema = Type.Object({
  created_unavailable_slot_count: Type.Number(),
  cancelled_appointment_count: Type.Number(),
});
export type MarkUnavailableRangeResponseObject = Static<typeof MarkUnavailableRangeResponseSchema>;

const WEEKLY_HOUR_PATTERN = '^([01]\\d|2[0-3]):[0-5]\\d$';

/**
 * `PATCH app/availability/weekly-day` (outil Pulse, carte de confirmation) : un jour du planning
 * hebdomadaire — fermer, ouvrir ou changer les heures — sans renvoyer toute la grille. Les
 * `description` sont lues par le modèle dans le schéma de l'outil.
 */
export const SetWeeklyDayRequestSchema = Type.Object(
  {
    availability_id: Type.Optional(
      Type.Integer({
        minimum: 1,
        description:
          "Planning visé (id de getAvailabilitiesForPro). Facultatif quand le pro n'a qu'un planning hebdomadaire.",
      }),
    ),
    weekday: Type.Integer({ minimum: 1, maximum: 7, description: 'Jour ISO : 1 = lundi … 7 = dimanche.' }),
    open: Type.Boolean({
      description:
        'false = jour fermé (plus aucun créneau ce jour-là) ; true = ouvert de start à end (un jour qui avait plusieurs plages, pause comprise, les garde bornées sur start–end).',
    }),
    start: Type.Optional(
      Type.String({ pattern: WEEKLY_HOUR_PATTERN, description: 'Ouverture « HH:MM » sur 24 h, requis si open.' }),
    ),
    end: Type.Optional(
      Type.String({
        pattern: WEEKLY_HOUR_PATTERN,
        description: 'Fermeture « HH:MM » sur 24 h (00:00 = minuit, fin de journée), requis si open.',
      }),
    ),
  },
  { additionalProperties: false },
);
export type SetWeeklyDayRequestObject = Static<typeof SetWeeklyDayRequestSchema>;

export const RestoreAvailabilityRangeRequestSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
});
export type RestoreAvailabilityRangeRequestObject = Static<typeof RestoreAvailabilityRangeRequestSchema>;

export const RestoreAvailabilityRangeResponseSchema = Type.Object({
  removed_unavailable_slot_count: Type.Number(),
});
export type RestoreAvailabilityRangeResponseObject = Static<typeof RestoreAvailabilityRangeResponseSchema>;

export const AvailabilityRangePreviewRequestSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
});
export type AvailabilityRangePreviewRequestObject = Static<typeof AvailabilityRangePreviewRequestSchema>;

export const AvailabilityRangePreviewResponseSchema = Type.Object({
  affected_availability_count: Type.Number(),
  overlapping_appointment_count: Type.Number(),
  existing_unavailable_slot_count: Type.Number(),
});
export type AvailabilityRangePreviewResponseObject = Static<typeof AvailabilityRangePreviewResponseSchema>;

export const BlockedAvailabilityChunksRequestSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  team_member_ids: Type.Optional(
    Type.Array(Type.Number(), {
      description:
        'Filter blocks affecting these team members. Result includes global blocks (team_member_id is null) since they affect every member.',
    }),
  ),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 50 })),
  offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type BlockedAvailabilityChunksRequestObject = Static<typeof BlockedAvailabilityChunksRequestSchema>;

export const BlockedAvailabilityChunkSchema = Type.Object({
  unavailable_slot_id: Type.Number(),
  availability_id: Type.Number(),
  availability_name: Type.Optional(Type.String()),
  start: Type.String({ format: 'date-time' }),
  start_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'start', is_short: true, with_time: true } }),
  ),
  end: Type.String({ format: 'date-time' }),
  end_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'end', is_short: true, with_time: true } }),
  ),
  time_range_formatted: Type.Optional(
    Type.String({ meta: { source_keys: ['start', 'end'], format_as: 'time-range', separator: ' -> ' } }),
  ),
});
export type BlockedAvailabilityChunkObject = Static<typeof BlockedAvailabilityChunkSchema>;

export const BlockedAvailabilityChunksResponseSchema = Type.Object({
  blocked_chunks: Type.Array(BlockedAvailabilityChunkSchema),
  total_count: Type.Number(),
});
export type BlockedAvailabilityChunksResponseObject = Static<typeof BlockedAvailabilityChunksResponseSchema>;
